import { Box, useTheme } from '@mui/material';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import MenuItemWrapper from '../../../../components/Dropdown/DropdownStyle';
import { VerticalDotsOutlineIcon } from '../../../../components/Icons/VerticalDotsIcon';
import MenuWrapper from '../../../../components/Menu/MenuStyle';
import UserDetailsContext from '../../../../contexts/UserDetailsContext/UserDetailsContext';
import useModal from '../../../../hooks/useModal/useModal';
import { Device, DeviceOperationsType } from '../../../../models/UserDevicesModels';
import ClickableLink from '../../../../components/Text/ClickableLink';

interface UserDetailsDeviceRowOptionsProps {
  device: Device;
  deleteDevice: (deviceId: string) => void;
  isPerformingAction: boolean;
}
function UserDetailsDeviceRowOptions({ device, deleteDevice, isPerformingAction }: UserDetailsDeviceRowOptionsProps) {
  const [t] = useTranslation();
  const { pathname } = useLocation();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const user = useContext(UserDetailsContext);
  const modal = useModal();

  /**
   * Handle button click
   * @param event
   */
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  /**
   * Handle menu close
   */
  const handleClose = () => {
    setAnchorEl(null);
  };

  /**
   * Displays the confirmation modal
   */
  const showConfirmationModal = () => {
    handleClose();
    modal.showModal({
      modalTitle: t('pages.deviceDetails.deleteDevice'),
      modalDescription: (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <span>{t('pages.deviceDetails.deleteDeviceMessage1')}</span>
          <span>{t('pages.deviceDetails.deleteDeviceMessage2')}</span>
        </Box>
      ),
      confirmationModal: true,
      confirmationButtonLabel: t('common.delete'),
      onAccept: (setOpen) => {
        setOpen(false);
        deleteDevice(device.id);
      },
    });
  };

  /**
   * Render delete option
   */
  const renderDeleteOption = () => {
    if (device?.deviceOperations && device.deviceOperations.includes(DeviceOperationsType.CAN_DELETE)) {
      return (
        <MenuItemWrapper
          onClick={showConfirmationModal}
          sx={{ color: theme.palette.primary.main }}
          disabled={sessionStorage.getItem('impersonateId') !== null}
        >
          {t('pages.deviceDetails.deleteDevice')}
        </MenuItemWrapper>
      );
    }
    return null;
  };

  return (
    <>
      <Box
        id={`box-${device.id}`}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          borderRadius: '0.375rem',
          width: '2.25rem',
          height: '2.25rem',
          flexShrink: 0,
          ':hover': { backgroundColor: theme.palette.monochrome.monoChrome11, cursor: 'pointer' },
        }}
        onClick={handleClick}
      >
        <VerticalDotsOutlineIcon stroke="#E60000" />
      </Box>
      <Box>
        <MenuWrapper
          id={`menu-${device.id}`}
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItemWrapper disabled={isPerformingAction}>
            <ClickableLink
              to={
                pathname.startsWith('/dashboard/devicesReport')
                  ? `/dashboard/devicesReport/userDetails/${user?.id}/deviceDetails/${device.id}`
                  : `/esimManager/userDetails/${user?.id}/deviceDetails/${device.id}?userName=${user?.firstName} ${user?.lastName}`
              }
              sx={{ textDecoration: 'none', width: '100%' }}
            >
              {t('common.table.viewDetails')}
            </ClickableLink>
          </MenuItemWrapper>
          {renderDeleteOption()}
        </MenuWrapper>
      </Box>
    </>
  );
}

export default UserDetailsDeviceRowOptions;
